<template>
  <div class="bottom-sheet-root">
    <transition name="bottom-sheet">
      <div
        v-if="visible"
        class="bottom-sheet"
      >
        <div
          class="bottom-sheet__backdrop"
          @click="close"
        />
        <div
          ref="panel"
          class="bottom-sheet__panel"
          role="dialog"
          aria-modal="true"
          :aria-label="title"
          tabindex="-1"
        >
          <div class="bottom-sheet__grabber" />
          <div
            v-if="title"
            class="bottom-sheet__header"
          >
            <span class="bottom-sheet__title">{{ title }}</span>
            <a
              class="bottom-sheet__close"
              aria-label="Close"
              @click="close"
            >
              <GjIcon
                name="Close"
                size="18"
              />
            </a>
          </div>
          <div class="bottom-sheet__body">
            <slot />
          </div>
          <div
            v-if="$slots.footer"
            class="bottom-sheet__footer"
          >
            <slot name="footer" />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
/**
 * Mobile bottom sheet: a modal that slides up from the bottom edge, which is where
 * a thumb already is. Used instead of a dropdown when the list of choices is longer
 * than a couple of items, so a phone header never has to grow to hold them.
 *
 * The root element is moved to <body> on mount. The sheet is `position: fixed`, and
 * a fixed element nested inside a `position: sticky` / transformed ancestor is
 * clipped by it - the sticky filter bar it is opened from would cut it off.
 */
export default {
  name: 'BottomSheet',
  model: {
    prop: 'visible',
    event: 'change',
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
  },
  watch: {
    visible(open) {
      if (open) {
        this.lockScroll()
        this.$nextTick(this.focusPanel)
      } else {
        this.unlockScroll()
        this.restoreFocus()
      }
    },
  },
  mounted() {
    document.body.appendChild(this.$el)
    document.addEventListener('keydown', this.onKeydown)
    if (this.visible) this.lockScroll()
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown)
    this.unlockScroll()
    if (this.$el.parentNode) this.$el.parentNode.removeChild(this.$el)
  },
  methods: {
    close() {
      this.$emit('change', false)
      this.$emit('close')
    },
    onKeydown(event) {
      if (this.visible && event.key === 'Escape') this.close()
    },
    lockScroll() {
      this.previousFocus = document.activeElement
      this.previousOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
    },
    unlockScroll() {
      if (this.previousOverflow === undefined) return
      document.body.style.overflow = this.previousOverflow
      this.previousOverflow = undefined
    },
    focusPanel() {
      // Keeps the on-screen keyboard closed and hands the sheet the keyboard focus.
      if (this.previousFocus && this.previousFocus.blur) this.previousFocus.blur()
      if (this.$refs.panel) this.$refs.panel.focus()
    },
    restoreFocus() {
      if (this.previousFocus && this.previousFocus.focus) this.previousFocus.focus()
      this.previousFocus = null
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variables/variables';

.bottom-sheet {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  // Above the fixed navbar and any sticky page header.
  z-index: 1060;

  display: flex;
  align-items: flex-end;

  &__backdrop {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: rgba($blue-900, 0.45);
  }

  &__panel {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    max-height: 85vh;
    padding-bottom: env(safe-area-inset-bottom);
    background-color: $white;
    border-radius: 16px 16px 0 0;
    box-shadow: 0 -4px 24px rgba($blue-700, 0.18);
    outline: none;
  }

  &__grabber {
    flex: 0 0 auto;
    width: 36px;
    height: 4px;
    margin: 8px auto 0;
    border-radius: 2px;
    background-color: $gray-1234;
  }

  &__header {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
  }

  &__title {
    font-size: 15px;
    font-weight: 600;
    color: $blue-900;
  }

  &__close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    margin-right: -6px;
    color: $blue-800;
  }

  &__body {
    flex: 1 1 auto;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  &__footer {
    flex: 0 0 auto;
    padding: 12px 16px;
    border-top: 1px solid $gray-1234;
  }
}

.bottom-sheet-enter-active,
.bottom-sheet-leave-active {
  transition: opacity 0.2s ease;

  .bottom-sheet__panel {
    transition: transform 0.25s ease;
  }
}

.bottom-sheet-enter,
.bottom-sheet-leave-to {
  opacity: 0;

  .bottom-sheet__panel {
    transform: translateY(100%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .bottom-sheet-enter-active,
  .bottom-sheet-leave-active {
    transition: none;

    .bottom-sheet__panel {
      transition: none;
    }
  }
}
</style>
